{
  "common": {
    "networkError": "Network error — check connection",
    "retry": "Retry",
    "cancel": "Cancel",
    "close": "Close",
    "delete": "Delete",
    "prev": "Prev",
    "next": "Next"
  },
  "statusLabels": {
    "pending": "Pending",
    "active": "Active",
    "failed": "Failed",
    "removing": "Removing"
  },
  "healthLabels": {
    "healthy": "healthy",
    "degraded": "degraded",
    "down": "down"
  },
  "header": {
    "title": "Platform brands",
    "description": "Multi-brand operation: each brand has a marketing site, ESP sender identity, and (optionally) a custom external domain (Caddy + auto-ACME Let's Encrypt, 48h CNAME verify, 30d delete grace).",
    "reservedBtn": "View reserved subdomains",
    "newBtn": "+ New brand"
  },
  "filter": {
    "searchPlaceholder": "Search by name or domain…",
    "statusLabel": "Status",
    "statusAll": "All"
  },
  "list": {
    "aria": "Brand list",
    "loading": "Loading brands…",
    "loadFail": "Failed to load: {{message}}",
    "empty": "No brands yet — click \"+ New brand\" to create the default brand.",
    "defaultBadge": "Default",
    "merchantsHealth_one": "{{count}} merchant · health {{health}}",
    "merchantsHealth_other": "{{count}} merchants · health {{health}}",
    "verifyBtn": "Verify",
    "deleteBtn": "Delete",
    "pagination": "Page {{page}} · {{total}} total"
  },
  "detail": {
    "aria": "Brand detail",
    "empty": "Select a brand on the left to view details, track DNS verification, or manage sender identity.",
    "loading": "Loading brand…",
    "loadFail": "Failed to load brand.",
    "slugLabel": "slug",
    "editBtn": "Edit",
    "closeBtn": "Close",
    "sectionsAria": "Brand sections",
    "tabOverview": "Overview",
    "tabVerify": "Verify DNS",
    "tabSender": "Sender identity",
    "fields": {
      "status": "Status",
      "tagline": "Tagline",
      "primaryColor": "Primary color",
      "supportEmail": "Support email",
      "termsUrl": "Terms URL",
      "privacyUrl": "Privacy URL"
    }
  },
  "toast": {
    "loadDetailFailTitle": "Failed to load brand detail",
    "brandInUseTitle": "Brand is in use by merchants",
    "brandInUseDesc_one": "{{count}} merchant references brand \"{{name}}\". Please reassign before deleting.",
    "brandInUseDesc_other": "{{count}} merchants reference brand \"{{name}}\". Please reassign before deleting.",
    "deletedTitle": "Brand deleted",
    "deleteFailTitle": "Failed to delete brand",
    "reassignReasonRequiredTitle": "Reassign reason required",
    "reassignReasonRequiredDesc": "Reason must be at least 5 characters.",
    "reassignSameTitle": "Target same as source",
    "reassignSameDesc": "Choose a target brand different from the source brand.",
    "reassignSuccessTitle_one": "Reassigned {{count}} merchant",
    "reassignSuccessTitle_other": "Reassigned {{count}} merchants",
    "reassignFailTitle": "Reassign failed"
  },
  "deleteDialog": {
    "title": "Delete brand?",
    "description": "Delete \"{{name}}\". If merchants still reference this brand, the system will require reassignment first. A 30-day delete grace applies to DNS records."
  },
  "reassign": {
    "title": "Reassign tagged merchants",
    "intro_one": "{{count}} merchant is tagged with brand \"{{name}}\". Choose a target brand and a reassign reason.",
    "intro_other": "{{count}} merchants are tagged with brand \"{{name}}\". Choose a target brand and a reassign reason.",
    "targetLabel": "Target brand",
    "targetPlaceholder": "— select brand —",
    "reasonLabel": "Reason (≥5 chars)",
    "submit": "Reassign",
    "submitting": "Reassigning…"
  },
  "reserved": {
    "title": "Reserved subdomains",
    "description": "The slugs below are blocked when creating a new brand. The backend enforces the same list (VG_DOMAIN_RESERVED). Update both places when adding a new slug."
  },
  "form": {
    "titleCreate": "New platform brand",
    "titleEdit": "Edit brand",
    "stepsAria": "Wizard steps",
    "themeDefaultName": "New brand",
    "steps": {
      "identity": "Identity",
      "domain": "Domain",
      "visual": "Visual",
      "legal": "Legal",
      "confirm": "Confirm"
    },
    "fields": {
      "displayName": "Brand display name",
      "slug": "Brand slug",
      "slugHint": "Slug used for internal URLs. The list applies.",
      "slugImmutable": "Slug immutable",
      "reservedChip": "Reserved",
      "reservedChipTitle": "\"{{slug}}\" is a reserved subdomain.",
      "tagline": "Tagline",
      "description": "Description",
      "primaryDomain": "Primary domain",
      "primaryDomainPlaceholder": "premium.example.com",
      "additionalDomains": "Additional domains",
      "additionalDomainsHint": "One domain per line (or comma-separated).",
      "logoUrl": "Logo URL",
      "primaryColor": "Primary color",
      "secondaryColor": "Secondary color",
      "legalEntityName": "Legal entity name",
      "legalAddress": "Legal address",
      "supportEmail": "Support email",
      "termsUrl": "Terms URL",
      "privacyUrl": "Privacy URL"
    },
    "confirm": {
      "heading": "Review & confirm",
      "note": "After creation, the system kicks off the CNAME verify cycle (Caddy + auto-ACME, 48h timeout per NG-19). The brand is redirected to the verify-dns page.",
      "displayName": "Display name",
      "slug": "Slug",
      "primaryDomain": "Primary domain",
      "primaryColor": "Primary color",
      "supportEmail": "Support email"
    },
    "validation": {
      "slugFormat": "Slug may only contain a-z, 0-9, and hyphens.",
      "slugReserved": "Slug is in the reserved list.",
      "domainInvalid": "Invalid domain."
    },
    "btn": {
      "back": "Back",
      "next": "Next",
      "saving": "Saving…",
      "saveChanges": "Save changes",
      "createBrand": "Create brand"
    },
    "toast": {
      "formHasErrorsTitle": "Form has errors",
      "formHasErrorsDesc": "Please go back to the previous steps and check.",
      "updatedTitle": "Brand updated",
      "createdTitle": "Brand created — moving to verify DNS",
      "updateFailTitle": "Failed to update brand",
      "createFailTitle": "Failed to create brand"
    }
  },
  "verify": {
    "aria": "DNS verification panel",
    "title": "DNS verify",
    "description": "Caddy + auto-ACME (Let's Encrypt) will issue an SSL certificate automatically once the CNAME is verified.",
    "cnameHeading": "CNAME record to create:",
    "type": "Type",
    "name": "Name",
    "value": "Value",
    "ttl": "TTL",
    "domainFallback": "(brand domain)",
    "verifiedAt": "Verified at {{date}}",
    "lastFailed": "The last verification failed. Check the CNAME and try again.",
    "waiting": "Waiting for verification…",
    "overdue": "48h deadline passed",
    "countdown": "{{hours}}h {{minutes}}m remaining",
    "recheckBtn": "Re-check now",
    "rechecking": "Checking…",
    "guideSummary": "DNS provider guides",
    "guideCloudflare": "Cloudflare — create a CNAME record",
    "guideGoDaddy": "GoDaddy — add a CNAME record",
    "guideNamecheap": "Namecheap — create a CNAME record",
    "toast": {
      "verifiedTitle": "CNAME verified successfully",
      "notMatchedTitle": "CNAME does not match yet",
      "observed": "Observed: {{target}}",
      "noRecord": "No CNAME record found yet. Please check your DNS provider.",
      "checkFailTitle": "Failed to check DNS"
    }
  },
  "sender": {
    "aria": "Sender identity",
    "title": "Sender identity (SPF / DKIM / DMARC)",
    "description": "Email from this brand must pass DMARC alignment. Manage aliases in detail in the Sender Identities module (task-44).",
    "manageBtn": "Manage sender identities →",
    "loading": "Loading sender identities…",
    "empty": "No sender identity for this brand yet. Create an alias from the Sender Identities module to see it here.",
    "replyToProvider": "reply-to {{replyTo}} · provider {{provider}}",
    "table": {
      "type": "Type",
      "name": "Name",
      "value": "Value"
    },
    "statusLabels": {
      "pending": "Pending",
      "verified": "Verified",
      "failed": "Failed"
    }
  },
  "theme": {
    "aria": "Theme preview",
    "title": "Live preview",
    "scope": "scope",
    "untitled": "Untitled brand",
    "startNow": "Start now",
    "note": "Live preview only renders the header — the full marketing page appears when task-65 (homepage middleware) ships the route"
  }
}
